// Utility functions for detecting duplicate requirements

/**
 * Extract key terms from text for semantic comparison
 * @param {string} text - Text to extract terms from
 * @returns {Set} - Set of normalized key terms
 */
function extractKeyTerms(text) {
  if (!text) return new Set();
  
  // Convert to lowercase and remove special characters
  const cleaned = text.toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  // Split into words and filter out common stop words
  const stopWords = new Set([
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
    'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the',
    'to', 'was', 'will', 'with', 'this', 'these', 'they', 'we', 'i',
    'you', 'me', 'my', 'our', 'your', 'their', 'them', 'him', 'her'
  ]);
  
  const words = cleaned.split(' ')
    .filter(word => word.length > 2 && !stopWords.has(word));
  
  return new Set(words);
}

/**
 * Calculate semantic similarity based on key terms overlap
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} - Similarity percentage (0-1)
 */
function calculateSemanticSimilarity(str1, str2) {
  const terms1 = extractKeyTerms(str1);
  const terms2 = extractKeyTerms(str2);
  
  if (terms1.size === 0 && terms2.size === 0) return 1;
  if (terms1.size === 0 || terms2.size === 0) return 0;
  
  // Calculate Jaccard similarity (intersection over union)
  const intersection = new Set([...terms1].filter(term => terms2.has(term)));
  const union = new Set([...terms1, ...terms2]);
  
  return intersection.size / union.size;
}

/**
 * Calculate similarity between two strings using multiple methods
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} - Similarity percentage (0-1)
 */
export function calculateSimilarity(str1, str2) {
  if (!str1 || !str2) return 0;
  
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 1;
  
  // Calculate Levenshtein-based similarity
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  
  if (longer.length === 0) return 1;
  
  const editDistance = levenshteinDistance(longer, shorter);
  const levenshteinSim = (longer.length - editDistance) / longer.length;
  
  // Calculate semantic similarity
  const semanticSim = calculateSemanticSimilarity(str1, str2);
  
  // Return the higher of the two similarities (more lenient approach)
  // This helps catch cases where word order differs but content is similar
  return Math.max(levenshteinSim, semanticSim);
}

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} s1 - First string
 * @param {string} s2 - Second string
 * @returns {number} - Edit distance
 */
function levenshteinDistance(s1, s2) {
  const matrix = Array(s2.length + 1).fill(null).map(() => Array(s1.length + 1).fill(null));
  
  for (let i = 0; i <= s1.length; i++) {
    matrix[0][i] = i;
  }
  
  for (let j = 0; j <= s2.length; j++) {
    matrix[j][0] = j;
  }
  
  for (let j = 1; j <= s2.length; j++) {
    for (let i = 1; i <= s1.length; i++) {
      const substitutionCost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,     // insertion
        matrix[j - 1][i] + 1,     // deletion
        matrix[j - 1][i - 1] + substitutionCost, // substitution
      );
    }
  }
  
  return matrix[s2.length][s1.length];
}

/**
 * Check if two requirements are potentially duplicates
 * @param {Object} req1 - First requirement
 * @param {Object} req2 - Second requirement
 * @param {Object} options - Detection options
 * @returns {Object|null} - Duplicate match info or null
 */
export function checkDuplicate(req1, req2, options = {}) {
  const {
    titleThreshold = 0.7,        // Lowered from 0.8 to catch more edge cases
    descriptionThreshold = 0.6,  // Lowered from 0.7 to catch more edge cases
    requireSameType = false
  } = options;
  
  // Skip self-comparison
  if (req1.id === req2.id) return null;
  
  // Check if same type is required
  if (requireSameType && req1.type !== req2.type) return null;
  
  // Calculate title similarity
  const titleSimilarity = calculateSimilarity(req1.title, req2.title);
  
  // Calculate description similarity
  const descriptionSimilarity = calculateSimilarity(req1.description, req2.description);
  
  // Check if either title or description meets threshold
  const isTitleDuplicate = titleSimilarity >= titleThreshold;
  const isDescriptionDuplicate = descriptionSimilarity >= descriptionThreshold;
  
  if (isTitleDuplicate || isDescriptionDuplicate) {
    const maxSimilarity = Math.max(titleSimilarity, descriptionSimilarity);
    const primaryMatch = titleSimilarity > descriptionSimilarity ? 'title' : 'description';
    
    return {
      req1,
      req2,
      titleSimilarity,
      descriptionSimilarity,
      maxSimilarity,
      primaryMatch,
      confidence: maxSimilarity >= 0.8 ? 'high' : maxSimilarity >= 0.65 ? 'medium' : 'low'
    };
  }
  
  return null;
}

/**
 * Find all duplicate pairs in a list of requirements
 * @param {Array} requirements - Array of requirement objects
 * @param {Object} options - Detection options
 * @returns {Array} - Array of duplicate match objects
 */
export function findDuplicates(requirements, options = {}) {
  if (!requirements || requirements.length < 2) return [];
  
  const duplicates = [];
  const processedPairs = new Set();
  
  for (let i = 0; i < requirements.length; i++) {
    for (let j = i + 1; j < requirements.length; j++) {
      const req1 = requirements[i];
      const req2 = requirements[j];
      
      // Create a unique pair identifier to avoid duplicate processing
      const pairId = [req1.id, req2.id].sort().join('-');
      if (processedPairs.has(pairId)) continue;
      processedPairs.add(pairId);
      
      const duplicate = checkDuplicate(req1, req2, options);
      if (duplicate) {
        duplicates.push(duplicate);
      }
    }
  }
  
  // Sort by similarity score (highest first)
  return duplicates.sort((a, b) => b.maxSimilarity - a.maxSimilarity);
}

/**
 * Group duplicates by their relationships
 * @param {Array} duplicates - Array of duplicate match objects
 * @returns {Array} - Array of duplicate groups
 */
export function groupDuplicates(duplicates) {
  if (!duplicates || duplicates.length === 0) return [];
  
  const groups = [];
  
  duplicates.forEach(duplicate => {
    const { req1, req2 } = duplicate;
    
    // Check if either requirement is already in a group
    let existingGroup = null;
    for (const group of groups) {
      if (group.requirements.some(req => req.id === req1.id || req.id === req2.id)) {
        existingGroup = group;
        break;
      }
    }
    
    if (existingGroup) {
      // Add to existing group
      if (!existingGroup.requirements.some(req => req.id === req1.id)) {
        existingGroup.requirements.push(req1);
      }
      if (!existingGroup.requirements.some(req => req.id === req2.id)) {
        existingGroup.requirements.push(req2);
      }
      existingGroup.matches.push(duplicate);
    } else {
      // Create new group
      groups.push({
        id: `group-${groups.length + 1}`,
        requirements: [req1, req2],
        matches: [duplicate],
        maxSimilarity: duplicate.maxSimilarity
      });
    }
  });
  
  // Sort groups by max similarity
  return groups.sort((a, b) => b.maxSimilarity - a.maxSimilarity);
}

/**
 * Get a human-readable description of the duplicate match
 * @param {Object} duplicate - Duplicate match object
 * @returns {string} - Description
 */
export function getDuplicateDescription(duplicate) {
  const { primaryMatch, confidence, titleSimilarity, descriptionSimilarity } = duplicate;
  
  const confidenceText = confidence === 'high' ? 'Very likely' : 
                        confidence === 'medium' ? 'Likely' : 'Possibly';
  
  const matchText = primaryMatch === 'title' ? 
    `similar titles (${Math.round(titleSimilarity * 100)}% match)` :
    `similar descriptions (${Math.round(descriptionSimilarity * 100)}% match)`;
  
  return `${confidenceText} duplicate: ${matchText}`;
} 